import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import authService, { LoginRequest, LoginResponse, UpdateProfileRequest } from '../service/auth.service';

export type User = {
  id: string;
  name: string;
  email: string;
  platform: string; // "web", "android", "ios" vb.
  createdAt: string; // ISO string preferred in API; convert to Date if needed
  avatarUrl?: string;
  role?: 'ADMIN' | 'MEMBER' | 'MANAGER' | string;
};

export type AuthState = {
  user: User | null;
  token: string | null;
};

export type LoginPayload = LoginRequest;

export type AuthContextType = {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => void;
  updateProfile: (payload: UpdateProfileRequest) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = 'auth_state';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(() => {
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      return stored ? JSON.parse(stored) : { user: null, token: null };
    } catch {
      return { user: null, token: null };
    }
  });

  useEffect(() => {
    try {
      const cleaned = localStorage.getItem('iscleaned');
      if (cleaned !== 'true') {
        setState({ token: null, user: null });
        localStorage.setItem('iscleaned', 'true');
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(state));
    } catch {}
  }, [state]);

  const login = useCallback(async ({ email, password }: LoginPayload) => {
    const res = (await authService.login({ email, password })) as LoginResponse;
    const mappedUser: User = {
      id: res.user.id,
      name: res.user.name,
      email: res.user.email,
      platform: res.user.platform,
      createdAt: res.user.createdAt,
      avatarUrl: res.user.avatarUrl,
      role: (res.user as any).role,
    };
    setState({ token: res.token, user: mappedUser });
  }, []);

  const logout = useCallback(() => {
    setState({ token: null, user: null });
  }, []);

  const updateProfile = useCallback(async ({ name, avatar }: UpdateProfileRequest) => {
    const current = state.user;
    if (!current) return;
    const updated = await authService.updateProfile(current.id, { name, avatar });
    setState((prev) => {
      if (!prev.user) return prev;
      const nextUser: User = {
        ...prev.user,
        name: updated.name ?? prev.user.name,
        platform: (updated as any).platform ?? prev.user.platform,
        createdAt: (updated as any).createdAt ?? prev.user.createdAt,
        email: prev.user.email,
        avatarUrl: (updated as any).avatarUrl || (updated as any).avatar || prev.user.avatarUrl,
        role: prev.user.role,
      };
      return { ...prev, user: nextUser };
    });
  }, [state.user]);

  const value = useMemo<AuthContextType>(
    () => ({
      user: state.user,
      token: state.token,
      isAuthenticated: Boolean(state.token),
      login,
      logout,
      updateProfile,
    }),
    [state.user, state.token, login, logout, updateProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
